import { useCallback, useRef } from 'react';
import { browserHistory } from 'react-router';
import appendQuery from 'append-query';
import recordEvent from 'platform/monitoring/record-event';
import { focusElement } from 'platform/utilities/ui';
import { LocationType } from '../constants';
import { stripSerializedArrayKeys } from '../utils/urlQuery';
import {
  LOCATION_INPUT_STATUS,
  validateLocationInput,
} from '../utils/validateLocationInput';

/** Handles form validation and submission. */
const useSearchSubmit = ({
  draftFormState,
  setDraftFormState,
  selectedServiceType,
  currentQuery,
  location,
  onChange,
  onSubmit,
  isMobile,
  mobileMapUpdateEnabled,
  selectMobileMapPin,
  setSearchInitiated,
  setSubmitErrors,
  clearSubmitErrors,
  clearSearchResults,
  checkLocationValidity,
}) => {
  // Track last submitted query to prevent duplicate submissions
  const lastQueryRef = useRef(null);
  // Track the last location string checked via the background geocoder so a
  // blocked submit (facility/service type still invalid) doesn't re-hit
  // Mapbox on every click while the location text hasn't changed (CMS #24836).
  const lastCheckedLocationRef = useRef(null);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      // Normalize "no service filter" values so null / undefined / '' all
      // collapse to null. They produce the same upstream search (all services),
      // so switching only between them (e.g. analytics 'default' -> 'all') is
      // not a new search and must not re-fire fl-search. This normalized value
      // is the effective query: it feeds the de-dup check below and is what we
      // forward to onChange/onSubmit, so Redux and the search never receive a
      // raw undefined/'' (e.g. an "All VA health services" pick with no id).
      const submittedServiceType = draftFormState.serviceType || null;

      // Trim so " Austin, TX " and "Austin, TX" are treated the same (#22751).
      const submittedSearchString = draftFormState.searchString?.trim() || '';

      // Reuse the Mapbox feature the address autosuggest already fetched — the
      // suggestion the user picked, or the first one offered when they submitted
      // without picking — so the search skips a second geocode (CMS #20560).
      // Only while the cached term still matches the string being submitted: if
      // the user edited the input afterward (or submitted before results
      // landed), leaving this null re-geocodes downstream.
      const cachedGeocode = draftFormState.locationGeocode;
      const reusableGeocodeFeature =
        cachedGeocode?.feature &&
        cachedGeocode.query?.trim() === submittedSearchString
          ? cachedGeocode.feature
          : null;

      // The form search is address-based (handleSearch -> genBBoxFromAddress)
      // and does not depend on map zoom — zoom-based re-search is the separate
      // "search this area" path. So zoom is intentionally excluded here: an
      // automatic zoom change after results render must not count as a new
      // query and re-fire fl-search.
      const isSameQuery =
        lastQueryRef.current &&
        draftFormState.facilityType === lastQueryRef.current.facilityType &&
        submittedServiceType === lastQueryRef.current.serviceType &&
        submittedSearchString === lastQueryRef.current.searchString;

      if (isSameQuery) {
        return;
      }

      // Validate all fields simultaneously so every error is visible at once.
      // Focus priority matches visual layout top-to-bottom:
      // location → facilityType → serviceType
      const errors = {};
      let firstErrorFocus = null;

      // Empty field → "required" error; present-but-unrecognizable → "invalid"
      // error (CMS #22751).
      const locationStatus = validateLocationInput(submittedSearchString);
      if (locationStatus === LOCATION_INPUT_STATUS.EMPTY) {
        errors.locationChanged = true;
        firstErrorFocus = firstErrorFocus || 'location';
      } else if (locationStatus === LOCATION_INPUT_STATUS.INVALID) {
        errors.locationInvalid = true;
        firstErrorFocus = firstErrorFocus || 'location';
      }

      if (!draftFormState.facilityType) {
        errors.facilityTypeChanged = true;
        firstErrorFocus = firstErrorFocus || 'facilityType';
      } else if (
        draftFormState.facilityType === LocationType.CC_PROVIDER &&
        (!draftFormState.serviceType || !selectedServiceType)
      ) {
        errors.serviceTypeChanged = true;
        firstErrorFocus = firstErrorFocus || 'serviceType';
      }

      if (Object.keys(errors).length > 0) {
        // A syntactically fine location (a real-looking ZIP or city/state)
        // only gets confirmed by the geocoder — which otherwise never runs
        // once another field blocks submit. Re-check it here in the
        // background so a fake-but-well-formed location (e.g. ZIP "00000")
        // surfaces its error now instead of requiring a second submit
        // (CMS #24836). Skipped if unchanged since the last check to avoid
        // re-hitting Mapbox on repeat clicks with the same broken form.
        if (
          locationStatus === LOCATION_INPUT_STATUS.VALID &&
          checkLocationValidity &&
          lastCheckedLocationRef.current !== submittedSearchString
        ) {
          lastCheckedLocationRef.current = submittedSearchString;
          // Hand over the cached feature so this check reuses it too — with one
          // available it needs no request at all (CMS #20560).
          checkLocationValidity(submittedSearchString, reusableGeocodeFeature);
        }

        // A blocked submit never runs a search, so clear any results from a
        // prior successful search — otherwise stale results linger alongside
        // the new error (CMS #22751).
        clearSearchResults?.();
        // Also drop the address from the URL so a reload doesn't replay the
        // now-cleared search via searchWithUrl (CMS #22751).
        if (location?.query?.address) {
          const restQuery = stripSerializedArrayKeys(location.query);
          delete restQuery.address;
          browserHistory.push(
            appendQuery(`/find-locations${location.pathname}`, restQuery),
          );
        }
        setSubmitErrors({ ...errors });
        setDraftFormState(prev => ({
          ...prev,
          ...errors,
          isValid: false,
          submitErrorLocation: !!errors.locationChanged,
          submitErrorLocationInvalid: !!errors.locationInvalid,
          submitErrorFacilityType: !!errors.facilityTypeChanged,
          submitErrorServiceType: !!errors.serviceTypeChanged,
        }));
        setTimeout(() => {
          if (firstErrorFocus === 'facilityType') {
            const vaSelect = document.querySelector('#facility-type-dropdown');
            if (vaSelect?.shadowRoot) {
              const internalSelect =
                vaSelect.shadowRoot.querySelector('select');
              if (internalSelect) {
                internalSelect.focus();
              }
            }
          } else if (firstErrorFocus === 'serviceType') {
            focusElement('#service-type-ahead-input');
          } else {
            focusElement('#street-city-state-zip');
          }
        }, 0);
        return;
      }

      clearSubmitErrors();
      setDraftFormState(prev => ({
        ...prev,
        searchString: submittedSearchString,
        submitErrorLocation: false,
        submitErrorLocationInvalid: false,
        submitErrorFacilityType: false,
        submitErrorServiceType: false,
      }));

      lastQueryRef.current = {
        facilityType: draftFormState.facilityType,
        serviceType: submittedServiceType,
        searchString: submittedSearchString,
      };

      onChange({
        facilityType: draftFormState.facilityType,
        serviceType: submittedServiceType,
        searchString: submittedSearchString,
        vamcServiceDisplay: draftFormState.vamcServiceDisplay,
      });

      // Record analytics with specialty display name for CC providers
      let analyticsServiceType = draftFormState.serviceType;
      const specialtyDisplayName =
        currentQuery.specialties?.[draftFormState.serviceType];

      if (
        draftFormState.facilityType === LocationType.CC_PROVIDER &&
        currentQuery.specialties &&
        specialtyDisplayName
      ) {
        analyticsServiceType = specialtyDisplayName;
      }

      // For VA health searches, the VAMC service autosuggest records *why* the
      // search ran without a user-selected specific service, so analytics can
      // tell these apart (see VAMCServiceAutosuggest's serviceTypeIntent):
      //   'all'               - user picked "All VA health services" without a
      //                         preceding search (a direct pick)
      //   'found_default'     - user typed a query that returned suggestions but
      //                         did not pick a specific one (incl. then picking
      //                         "All VA health services")
      //   'not_found_default' - user typed a query that returned no suggestions
      //   'default'           - user did not run a service search (never typed,
      //                         only opened the dropdown, or cleared to blank)
      const isHealth = draftFormState.facilityType === LocationType.HEALTH;
      if (isHealth) {
        if (draftFormState.serviceTypeIntent) {
          analyticsServiceType = draftFormState.serviceTypeIntent;
        } else if (!draftFormState.serviceType) {
          analyticsServiceType = 'default';
        }
      }

      // Independent VA-health service-type interaction facets (each 1/0). More
      // than one may be set on a single event — e.g. a typed query with no
      // results sets both -no-results-input and -no-selection-search. Only
      // VA-health searches go through the service-type autosuggest, so these are
      // all 0 for other facility types. effectiveIntent mirrors how
      // analyticsServiceType resolves: an explicit intent wins, otherwise a
      // chosen specific service ('specific') or nothing typed/selected
      // ('default'). selectionMethod ('mouse' | 'keyboard') is set by
      // VAMCServiceAutosuggest for any dropdown pick.
      let effectiveIntent = 'default';
      if (draftFormState.serviceTypeIntent) {
        effectiveIntent = draftFormState.serviceTypeIntent;
      } else if (draftFormState.serviceType) {
        effectiveIntent = 'specific';
      }
      const method = draftFormState.selectionMethod;
      const bool = value => (value ? 1 : 0);
      const svcTypeFacets = {
        // user typed but did not pick a specific option
        'fl-search-svc-type-no-selection-search': bool(
          isHealth &&
            (effectiveIntent === 'found_default' ||
              effectiveIntent === 'not_found_default'),
        ),
        // user input yielded no autosuggest options
        'fl-search-svc-type-no-results-input': bool(
          isHealth && effectiveIntent === 'not_found_default',
        ),
        // user did not type and "All VA health services" was searched (explicit
        // "All" pick or never-typed default-to-All submit)
        'fl-search-svc-type-no-results-search-all': bool(
          isHealth &&
            (effectiveIntent === 'all' || effectiveIntent === 'default'),
        ),
        // user selected an autosuggest option via dropdown (mouse click)
        'fl-search-svc-type-selection-search': bool(
          isHealth && method === 'mouse',
        ),
        // user selected an autosuggest option via keyboard (Enter)
        'fl-search-svc-type-via-keyboard': bool(
          isHealth && method === 'keyboard',
        ),
      };

      // Successful submission analytics
      recordEvent({
        event: 'fl-search',
        'fl-search-fac-type': draftFormState.facilityType,
        'fl-search-svc-type': analyticsServiceType,
        'fl-current-zoom-depth': currentQuery.zoomLevel,
        ...svcTypeFacets,
      });

      if (isMobile && mobileMapUpdateEnabled) {
        selectMobileMapPin(null);
      }

      setSearchInitiated(true);
      onSubmit({
        facilityType: draftFormState.facilityType,
        serviceType: submittedServiceType,
        searchString: submittedSearchString,
        vamcServiceDisplay: draftFormState.vamcServiceDisplay,
        geocodeFeature: reusableGeocodeFeature,
      });
    },
    [
      draftFormState,
      setDraftFormState,
      selectedServiceType,
      currentQuery.zoomLevel,
      currentQuery.specialties,
      location,
      onChange,
      onSubmit,
      isMobile,
      mobileMapUpdateEnabled,
      selectMobileMapPin,
      setSearchInitiated,
      setSubmitErrors,
      clearSubmitErrors,
      clearSearchResults,
      checkLocationValidity,
    ],
  );

  return { handleSubmit };
};

export default useSearchSubmit;
